<template>
  <v-dialog v-model="innerValue" persistent max-width="520px" content-class="start-order-dialog">
    <v-card class="start-card">
      <div class="start-dialog-header">
        <div class="step-icon">
          <v-icon color="primary">{{ step === "details" ? "mdi-account-outline" : "mdi-message-lock-outline" }}</v-icon>
        </div>
        <div class="header-copy">
          <div class="step-label">{{ recognizing ? "WELCOME BACK" : `STEP ${step === "details" ? "1" : "2"} OF 2` }}</div>
          <div class="start-dialog-title">{{ recognizing ? "Recognizing This Browser" : step === "details" ? "Start Your Order" : "Verify Your Phone" }}</div>
          <div class="start-dialog-subtitle">
            <template v-if="recognizing">Checking your saved verification.</template>
            <template v-else-if="step === 'details'">Enter your contact information to continue.</template>
            <template v-else>We sent a code to the phone ending in <strong>{{ phoneLast4 }}</strong>.</template>
          </div>
        </div>
        <v-btn icon small aria-label="Close" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="start-dialog-body">
        <div v-if="recognizing" class="recognizing"><v-progress-circular indeterminate color="primary" /><span>Opening your order…</span></div>
        <template v-else-if="step === 'details'">
          <v-text-field v-model.trim="form.name" label="Your Name" autocomplete="name" outlined :error-messages="errors.name" />
          <v-text-field v-model.trim="form.businessName" label="Business Name" autocomplete="organization" outlined :error-messages="errors.businessName" />
          <v-text-field
            v-model.trim="form.phoneNumber"
            label="Mobile Phone Number"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            placeholder="(555) 555-1234"
            outlined
            :error-messages="errors.phoneNumber"
            hint="We will text this number a verification code."
            persistent-hint
          />
          <v-text-field
            v-model.trim="form.email"
            label="Email Address"
            type="email"
            inputmode="email"
            autocomplete="email"
            outlined
            :error-messages="errors.email"
            @keyup.enter="sendCode"
          />
        </template>

        <template v-else>
          <div class="otp-copy">Enter the 6-digit code. You will continue automatically after the last digit.</div>
          <v-otp-input
            ref="otpInput"
            v-model="code"
            class="otp-input"
            :length="6"
            type="number"
            @finish="verifyCode"
          />
          <div v-if="otpError.length" class="otp-error" role="alert">{{ otpError[0] }}</div>
          <div class="otp-keypad" aria-label="Verification code keypad">
            <v-btn v-for="digit in keypadDigits" :key="digit" class="keypad-key" outlined @click="appendDigit(digit)">{{ digit }}</v-btn>
            <span />
            <v-btn class="keypad-key" outlined @click="appendDigit('0')">0</v-btn>
            <v-btn class="keypad-key" text aria-label="Delete last digit" @click="deleteDigit"><v-icon>mdi-backspace-outline</v-icon></v-btn>
          </div>
          <div class="verification-actions">
            <v-btn text small color="primary" @click="editPhone"><v-icon left small>mdi-pencil</v-icon>Change number</v-btn>
            <v-btn text small color="primary" :disabled="resendSeconds > 0 || sendingCode" :loading="sendingCode" @click="resendCode">
              {{ resendSeconds > 0 ? `Resend in ${formatCountdown(resendSeconds)}` : "Resend code" }}
            </v-btn>
          </div>
          <v-alert dense text type="info" class="privacy-note">Message and data rates may apply. The code expires in 10 minutes.</v-alert>
        </template>
      </v-card-text>

      <v-divider v-if="!recognizing" />
      <v-card-actions v-if="!recognizing" class="start-dialog-actions">
        <v-btn text @click="step === 'details' ? close() : editPhone()">{{ step === "details" ? "Cancel" : "Back" }}</v-btn>
        <v-spacer />
        <v-btn
          rounded
          large
          :dark="darkMode"
          :color="buttonColor"
          :loading="loading || sendingCode || verifying"
          :disabled="step === 'otp' && code.length !== 6"
          @click="step === 'details' ? sendCode() : verifyCode()"
        >{{ step === "details" ? "Send Code" : "Verify & Continue" }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import orderService from "./service";

export default {
  name: "PublicCustomerStartDialog",
  props: {
    value: { type: Boolean, default: false },
    businessCode: { type: [String, Number], required: true },
    buttonColor: { type: String, default: "primary" },
    darkMode: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      step: "details",
      form: { name: "", businessName: "", phoneNumber: "", email: "" },
      errors: { name: [], businessName: [], phoneNumber: [], email: [] },
      code: "",
      otpError: [],
      challengeId: null,
      phoneLast4: "",
      resendSeconds: 0,
      countdownTimer: null,
      sendingCode: false,
      verifying: false,
      recognizing: false,
      keypadDigits: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    };
  },
  computed: {
    innerValue: {
      get() { return this.value; },
      set(value) { this.$emit("input", value); },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) { if (open) this.openDialog(); },
    },
  },
  created() {
    const saved = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      this.form = {
        name: parsed.name || "",
        businessName: parsed.businessName || parsed.name || "",
        phoneNumber: parsed.phoneNumber || "",
        email: parsed.email || "",
      };
    } catch (error) {
      localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`);
    }
  },
  beforeDestroy() { clearInterval(this.countdownTimer); },
  methods: {
    close() { this.innerValue = false; this.resetVerification(); },
    trustedTokenKey() { return `${process.env.VUE_APP_STORAGE_PREFIX}publicOrderTrustedDevice:${this.businessCode}`; },
    readTrustedToken() {
      const stored = localStorage.getItem(this.trustedTokenKey());
      if (stored) return stored;
      const name = `${this.trustedTokenKey()}=`;
      const cookie = document.cookie.split("; ").find(item => item.startsWith(name));
      return cookie ? decodeURIComponent(cookie.slice(name.length)) : "";
    },
    saveTrustedToken(token) {
      localStorage.setItem(this.trustedTokenKey(), token);
      document.cookie = `${this.trustedTokenKey()}=${encodeURIComponent(token)}; Max-Age=315360000; Path=/; SameSite=Lax`;
    },
    clearTrustedToken() {
      localStorage.removeItem(this.trustedTokenKey());
      document.cookie = `${this.trustedTokenKey()}=; Max-Age=0; Path=/; SameSite=Lax`;
    },
    async openDialog() {
      this.resetVerification();
      const token = this.readTrustedToken();
      if (!token) return;
      this.recognizing = true;
      try {
        const response = await orderService.recognizePublicOrderBrowser(this.businessCode, token);
        localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`, JSON.stringify(response.data.customerInfo));
        this.$emit("continue", response.data);
        this.innerValue = false;
      } catch (error) {
        this.clearTrustedToken();
      } finally {
        this.recognizing = false;
      }
    },
    resetVerification() {
      this.step = "details";
      this.code = "";
      this.otpError = [];
      this.challengeId = null;
      this.resendSeconds = 0;
      clearInterval(this.countdownTimer);
      this.recognizing = false;
    },
    phoneDigits() { return String(this.form.phoneNumber || "").replace(/\D/g, ""); },
    validate() {
      this.errors = { name: [], businessName: [], phoneNumber: [], email: [] };
      if (this.form.name.trim().length < 2) this.errors.name = ["Enter your full name"];
      if (this.form.businessName.trim().length < 2) this.errors.businessName = ["Enter a valid business name"];
      const digits = this.phoneDigits();
      if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith("1")))) {
        this.errors.phoneNumber = ["Enter a valid 10-digit US mobile number"];
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
        this.errors.email = ["Enter a valid email address"];
      }
      return !Object.values(this.errors).some(messages => messages.length);
    },
    async sendCode() {
      if (!this.validate()) return;
      this.sendingCode = true;
      try {
        const response = await orderService.sendPublicOrderVerification(this.businessCode, this.form);
        this.challengeId = response.data.challengeId;
        this.phoneLast4 = response.data.phoneLast4;
        this.step = "otp";
        this.startCountdown(response.data.resendAfterSeconds || 120);
        this.$nextTick(() => this.$refs.otpInput && this.$refs.otpInput.focus());
      } catch (error) {
        const data = error.response && error.response.data;
        const message = (data && data.message) || "Unable to send a code to this number";
        if (this.step === "otp") this.otpError = [message];
        else this.errors.phoneNumber = [message];
        if (data && data.retryAfterSeconds) this.startCountdown(data.retryAfterSeconds);
      } finally {
        this.sendingCode = false;
      }
    },
    async resendCode() { await this.sendCode(); },
    appendDigit(digit) {
      if (this.verifying || this.code.length >= 6) return;
      this.code = `${this.code}${digit}`;
      this.otpError = [];
      if (this.code.length === 6) this.$nextTick(this.verifyCode);
    },
    deleteDigit() { if (!this.verifying) this.code = this.code.slice(0, -1); },
    async verifyCode() {
      if (this.code.length !== 6) { this.otpError = ["Enter the 6-digit code"]; return; }
      this.verifying = true;
      try {
        const response = await orderService.verifyPublicOrderPhone(this.businessCode, {
          challengeId: this.challengeId,
          phoneNumber: this.form.phoneNumber,
          code: this.code,
        });
        const customerInfo = { ...this.form, phoneNumber: response.data.phoneNumber };
        localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`, JSON.stringify(customerInfo));
        this.saveTrustedToken(response.data.trustedDeviceToken);
        this.$emit("continue", { customerInfo, verificationToken: response.data.verificationToken });
        this.innerValue = false;
      } catch (error) {
        const data = error.response && error.response.data;
        this.otpError = [(data && data.message) || "The verification code is incorrect"];
      } finally {
        this.verifying = false;
      }
    },
    editPhone() { this.step = "details"; this.code = ""; this.otpError = []; },
    startCountdown(seconds) {
      this.resendSeconds = Number(seconds || 0);
      clearInterval(this.countdownTimer);
      this.countdownTimer = setInterval(() => {
        if (this.resendSeconds <= 1) { this.resendSeconds = 0; clearInterval(this.countdownTimer); }
        else this.resendSeconds -= 1;
      }, 1000);
    },
    formatCountdown(seconds) { return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`; },
  },
};
</script>

<style scoped>
.start-card{border-radius:22px!important;overflow:hidden}.start-dialog-header{padding:20px;display:flex;align-items:flex-start;gap:12px;border-bottom:1px solid #e2e8f0}.step-icon{width:44px;height:44px;flex:none;border-radius:14px;background:#eff6ff;display:flex;align-items:center;justify-content:center}.header-copy{flex:1;min-width:0}.step-label{font-size:10px;font-weight:900;letter-spacing:.12em;color:#64748b}.start-dialog-title{font-size:21px;font-weight:900;color:#0f172a}.start-dialog-subtitle{font-size:13px;line-height:1.45;color:#64748b;margin-top:3px}.start-dialog-body{padding:20px!important}.recognizing{min-height:180px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;color:#475569;font-weight:700}.otp-copy{text-align:center;color:#475569;font-size:14px;margin:4px 0 14px}.otp-input{max-width:420px;margin:0 auto}.otp-error{text-align:center;color:#d32f2f;font-size:13px;margin:8px 0}.otp-keypad{max-width:330px;margin:16px auto 6px;display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.keypad-key{height:54px!important;font-size:22px!important;font-weight:800!important;border-radius:12px!important}.verification-actions{display:flex;justify-content:space-between;align-items:center;margin-top:10px}.privacy-note{font-size:12px;margin:16px 0 0}.start-dialog-actions{padding:14px 20px!important;background:#f8fafc}@media(max-width:600px){.start-order-dialog{margin:8px!important}.start-card{border-radius:18px!important}.start-dialog-header{padding:14px}.start-dialog-body{padding:14px!important}.otp-keypad{gap:8px}.keypad-key{height:58px!important}.start-dialog-actions{padding:12px 14px!important;position:sticky;bottom:0}.start-dialog-actions .v-btn:last-child{min-height:48px}.verification-actions{align-items:stretch;flex-direction:column;gap:4px}.verification-actions .v-btn{width:100%}}
</style>
